import { Lunar } from 'lunar-javascript';

export type YaoLine = 'yang' | 'yin';

export function toggleYao(line: YaoLine): YaoLine {
  return line === 'yang' ? 'yin' : 'yang';
}

export enum Trigram {
  qian = 1,
  dui = 2,
  li = 3,
  zhen = 4,
  xun = 5,
  kan = 6,
  gen = 7,
  kun = 8
}

export function trigramNameZh(t: Trigram): string {
  switch (t) {
    case Trigram.qian: return '乾';
    case Trigram.dui: return '兑';
    case Trigram.li: return '离';
    case Trigram.zhen: return '震';
    case Trigram.xun: return '巽';
    case Trigram.kan: return '坎';
    case Trigram.gen: return '艮';
    case Trigram.kun: return '坤';
  }
}

export function trigramLines(t: Trigram): YaoLine[] {
  switch (t) {
    case Trigram.qian: return ['yang', 'yang', 'yang'];
    case Trigram.dui: return ['yang', 'yang', 'yin'];
    case Trigram.li: return ['yang', 'yin', 'yang'];
    case Trigram.zhen: return ['yang', 'yin', 'yin'];
    case Trigram.xun: return ['yin', 'yang', 'yang'];
    case Trigram.kan: return ['yin', 'yang', 'yin'];
    case Trigram.gen: return ['yin', 'yin', 'yang'];
    case Trigram.kun: return ['yin', 'yin', 'yin'];
  }
}

export interface Hexagram {
  upper: Trigram;
  lower: Trigram;
  movingLine?: number;
}

export function hexagramLines(h: Hexagram): YaoLine[] {
  return [...trigramLines(h.lower), ...trigramLines(h.upper)];
}

export function hexagramDescriptionZh(h: Hexagram): string {
  return `上${trigramNameZh(h.upper)} 下${trigramNameZh(h.lower)}`;
}

export function hexagramChanged(h: Hexagram): Hexagram {
  if (!h.movingLine || h.movingLine < 1 || h.movingLine > 6) return h;
  const lines = hexagramLines(h);
  const idx = h.movingLine - 1;
  const toggled = [...lines];
  toggled[idx] = toggleYao(toggled[idx]);
  const lowerLines = toggled.slice(0, 3);
  const upperLines = toggled.slice(3, 6);
  const newLower = trigramFromLines(lowerLines) ?? h.lower;
  const newUpper = trigramFromLines(upperLines) ?? h.upper;
  return { upper: newUpper, lower: newLower, movingLine: h.movingLine };
}

export function hexagramMutual(h: Hexagram): Hexagram {
  const lines = hexagramLines(h);
  const lowerLines = [lines[1], lines[2], lines[3]];
  const upperLines = [lines[2], lines[3], lines[4]];
  const newLower = trigramFromLines(lowerLines) ?? h.lower;
  const newUpper = trigramFromLines(upperLines) ?? h.upper;
  return { upper: newUpper, lower: newLower };
}

export function trigramFromLines(lines: YaoLine[]): Trigram | undefined {
  if (lines.length !== 3) return undefined;
  for (const tri of Object.values(Trigram).filter(v => typeof v === 'number') as number[]) {
    const t = tri as Trigram;
    if (arraysEqual(trigramLines(t), lines)) return t;
  }
  return undefined;
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export enum FiveElement {
  wood = '木',
  fire = '火',
  earth = '土',
  metal = '金',
  water = '水'
}

export function trigramElement(t: Trigram): FiveElement {
  switch (t) {
    case Trigram.qian:
    case Trigram.dui:
      return FiveElement.metal;
    case Trigram.li:
      return FiveElement.fire;
    case Trigram.zhen:
    case Trigram.xun:
      return FiveElement.wood;
    case Trigram.kan:
      return FiveElement.water;
    case Trigram.gen:
    case Trigram.kun:
      return FiveElement.earth;
  }
}

export function generates(e: FiveElement): FiveElement {
  switch (e) {
    case FiveElement.wood: return FiveElement.fire;
    case FiveElement.fire: return FiveElement.earth;
    case FiveElement.earth: return FiveElement.metal;
    case FiveElement.metal: return FiveElement.water;
    case FiveElement.water: return FiveElement.wood;
  }
}

export function overcomes(e: FiveElement): FiveElement {
  switch (e) {
    case FiveElement.wood: return FiveElement.earth;
    case FiveElement.earth: return FiveElement.water;
    case FiveElement.water: return FiveElement.fire;
    case FiveElement.fire: return FiveElement.metal;
    case FiveElement.metal: return FiveElement.wood;
  }
}

/**
 * 体用分派 —— 梅花正统法度：**以动爻所在之卦为用卦（所问之事），另一卦为体卦（求测之人）**。
 *
 *   动爻 1~3 落在下卦 → 下卦为用、上卦为体
 *   动爻 4~6 落在上卦 → 上卦为用、下卦为体
 *
 * 函数名即义：默认（下卦动）体在上、用在下；动爻若落在「用卦」之外的上卦，
 * 则体用互换。必须与 core/tiyong.ts 的 assignTiYong() 完全一致 —— 二者过去
 * 相互矛盾，会让「起卦页」与「体用盘」显示相反的结论。
 */
export function bodyUseSwapIfMovingInUse(h: Hexagram): { body: Trigram; use: Trigram; swapped: boolean } {
  if (h.movingLine && h.movingLine >= 4 && h.movingLine <= 6) {
    return { body: h.lower, use: h.upper, swapped: true };
  }
  return { body: h.upper, use: h.lower, swapped: false };
}

export function bodyUseRelationText(body: Trigram, use: Trigram): string {
  const b = trigramElement(body);
  const u = trigramElement(use);
  if (b === u) return '比和';
  if (generates(b) === u) return '体生用';
  if (generates(u) === b) return '用生体';
  if (overcomes(b) === u) return '体克用';
  if (overcomes(u) === b) return '用克体';
  return '相畏';
}

export function getMutualBodyUse(mutual: Hexagram, originalMovingLine?: number): { body: Trigram, use: Trigram } {
  // If moving line is 4-6, Body is Lower.
  if (originalMovingLine && originalMovingLine >= 4 && originalMovingLine <= 6) {
    return { body: mutual.lower, use: mutual.upper };
  }
  // If moving line is 1-3 (or undefined default), Body is Upper.
  return { body: mutual.upper, use: mutual.lower };
}

export enum EarthlyBranch {
  zi = '子',
  chou = '丑',
  yin = '寅',
  mao = '卯',
  chen = '辰',
  si = '巳',
  wu = '午',
  wei = '未',
  shen = '申',
  you = '酉',
  xu = '戌',
  hai = '亥'
}

export function earthlyBranchElement(branch: EarthlyBranch): FiveElement {
  switch (branch) {
    case EarthlyBranch.yin:
    case EarthlyBranch.mao:
      return FiveElement.wood;
    case EarthlyBranch.si:
    case EarthlyBranch.wu:
      return FiveElement.fire;
    case EarthlyBranch.shen:
    case EarthlyBranch.you:
      return FiveElement.metal;
    case EarthlyBranch.hai:
    case EarthlyBranch.zi:
      return FiveElement.water;
    case EarthlyBranch.chen:
    case EarthlyBranch.wei:
    case EarthlyBranch.xu:
    case EarthlyBranch.chou:
      return FiveElement.earth;
  }
}

const BOUNDARIES = [
  { month: 1, day: 5, branch: EarthlyBranch.chou },
  { month: 2, day: 4, branch: EarthlyBranch.yin },
  { month: 3, day: 6, branch: EarthlyBranch.mao },
  { month: 4, day: 5, branch: EarthlyBranch.chen },
  { month: 5, day: 5, branch: EarthlyBranch.si },
  { month: 6, day: 6, branch: EarthlyBranch.wu },
  { month: 7, day: 7, branch: EarthlyBranch.wei },
  { month: 8, day: 8, branch: EarthlyBranch.shen },
  { month: 9, day: 8, branch: EarthlyBranch.you },
  { month: 10, day: 8, branch: EarthlyBranch.xu },
  { month: 11, day: 7, branch: EarthlyBranch.hai },
  { month: 12, day: 7, branch: EarthlyBranch.zi }
];

/**
 * 月令（节气月）—— 以北八区（东八区）节气交节当日 12:00 为界，精确到时刻。
 *
 * 锚点时刻 = Date.UTC(y, m-1, d, 4, 0, 0)  即北京时间 m 月 d 日 12:00。
 * 比较时必须使用**原始时刻** date.getTime()，不可先把时间平移 +8h 再与未平移的
 * 锚点比较 —— 那样会让换令时刻提前 8 小时（实测寒露、立春、小寒三处皆提前）。
 * 锚点年份取北京时间所属年份；元旦至小寒之间尚无锚点落入，落到子月。
 */
export function yuelingBranch(date: Date): EarthlyBranch {
  const utc8 = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  const year = utc8.getUTCFullYear();
  const anchors = BOUNDARIES.map(b => ({
    time: Date.UTC(year, b.month - 1, b.day, 4, 0, 0),
    branch: b.branch
  })).sort((a, b) => a.time - b.time);
  const t = date.getTime();
  for (let i = anchors.length - 1; i >= 0; i--) {
    if (t >= anchors[i].time) {
      return anchors[i].branch;
    }
  }
  return EarthlyBranch.zi;
}

export function yuelingWithElement(date: Date): string {
  const branch = yuelingBranch(date);
  const element = earthlyBranchElement(branch);
  return `${branch}月 · ${branch}（${element}）`;
}

/** 地支序数：子1、丑2 …… 亥12（《梅花易数》卷一“年月日时起例”） */
const BRANCH_ORDINAL: Record<string, number> = {
  子: 1, 丑: 2, 寅: 3, 卯: 4, 辰: 5, 巳: 6,
  午: 7, 未: 8, 申: 9, 酉: 10, 戌: 11, 亥: 12
};

/**
 * 年月日时起卦（《梅花易数》卷一）：
 *
 *   年数 = 年支序数（子1…亥12；以农历正月初一换年）
 *   月数 = 农历月（闰月取同序月数，如闰二月仍作 2）
 *   日数 = 农历日（初一1…三十30）
 *   时数 = 时支序数（子1…亥12；23:00 起子时，历日不提前换日）
 *
 *   上卦 = (年 + 月 + 日) ÷ 8 取余
 *   下卦 = (年 + 月 + 日 + 时) ÷ 8 取余
 *   动爻 = (年 + 月 + 日 + 时) ÷ 6 取余
 *
 * 分钟不参与起卦；同一时辰内起卦结果相同。
 */
export function generateHexagramFromDate(date: Date): Hexagram {
  const lunar = Lunar.fromDate(date);
  const yearNumber = BRANCH_ORDINAL[lunar.getYearZhi()] ?? 1;
  const monthNumber = Math.abs(lunar.getMonth());
  const dayNumber = lunar.getDay();
  const hourNumber = BRANCH_ORDINAL[lunar.getTimeZhi()] ?? 1;

  const sumYMD = yearNumber + monthNumber + dayNumber;
  const sumYMDH = sumYMD + hourNumber;
  const upperIndex = modToRange(sumYMD, 8);
  const lowerIndex = modToRange(sumYMDH, 8);
  const moving = modToRange(sumYMDH, 6);
  const upper = (Trigram as any)[Trigram[upperIndex]] as Trigram ?? Trigram.qian;
  const lower = (Trigram as any)[Trigram[lowerIndex]] as Trigram ?? Trigram.kun;
  return { upper, lower, movingLine: moving };
}

function modToRange(value: number, mod: number): number {
  const r = value % mod;
  return r === 0 ? mod : r;
}

export interface HexagramDTO {
  upper: string;
  lower: string;
  movingLine?: number;
}

export function toHexagramDTO(h: Hexagram): HexagramDTO {
  return {
    upper: trigramNameZh(h.upper),
    lower: trigramNameZh(h.lower),
    movingLine: h.movingLine
  };
}

export function getTrigramByName(name: string): Trigram | undefined {
  switch (name) {
    case '乾': return Trigram.qian;
    case '兑': return Trigram.dui;
    case '离': return Trigram.li;
    case '震': return Trigram.zhen;
    case '巽': return Trigram.xun;
    case '坎': return Trigram.kan;
    case '艮': return Trigram.gen;
    case '坤': return Trigram.kun;
    default: return undefined;
  }
}

export function fromHexagramDTO(dto: HexagramDTO): Hexagram {
  const upper = getTrigramByName(dto.upper) ?? Trigram.qian;
  const lower = getTrigramByName(dto.lower) ?? Trigram.kun;
  return {
    upper,
    lower,
    movingLine: dto.movingLine
  };
}
